import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/api";
import { toast } from "sonner";

const Ctx = createContext(null);
export const useApp = () => useContext(Ctx);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pk_cart") || "[]"); } catch { return []; }
  });
  const [wishlistIds, setWishlistIds] = useState([]);
  const [settings, setSettings] = useState({ brand: "DivineCart", whatsapp: "+919999999999" });

  const persistCart = (next) => {
    setCart(next);
    localStorage.setItem("pk_cart", JSON.stringify(next));
  };

  const loadMe = useCallback(async () => {
    const t = localStorage.getItem("pk_token");
    if (!t) { setAuthLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      const wl = await api.get("/wishlist").catch(() => ({ data: [] }));
      setWishlistIds((wl.data || []).map((p) => p.id));
    } catch {
      localStorage.removeItem("pk_token"); setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); api.get("/settings").then(({data}) => setSettings(s => ({...s, ...data}))).catch(()=>{}); }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("pk_token", data.token);
    setUser(data.user);
    toast.success(`Welcome back, ${data.user.name}!`);
    await loadMe();
    return data.user;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("pk_token", data.token);
    setUser(data.user);
    toast.success("Account created — Namaste!");
    return data.user;
  };
  const logout = () => { localStorage.removeItem("pk_token"); setUser(null); toast("Logged out"); };

  const addToCart = (item, qty = 1) => {
    const idx = cart.findIndex((c) => c.product_id === item.product_id && c.kit === (item.kit || false));
    let next;
    if (idx >= 0) { next = [...cart]; next[idx].qty += qty; }
    else next = [...cart, { ...item, qty }];
    persistCart(next);
    toast.success(`${item.name} added to cart`);
  };
  const updateQty = (product_id, qty) => {
    const next = cart.map((c) => (c.product_id === product_id ? { ...c, qty } : c)).filter((c) => c.qty > 0);
    persistCart(next);
  };
  const removeFromCart = (product_id) => persistCart(cart.filter((c) => c.product_id !== product_id));
  const clearCart = () => persistCart([]);

  const toggleWishlist = async (pid) => {
    if (!user) { toast.error("Please login to save to wishlist"); return; }
    try {
      const { data } = await api.post(`/wishlist/${pid}`);
      setWishlistIds((prev) => data.added ? [...prev, pid] : prev.filter((x) => x !== pid));
      toast.success(data.added ? "Added to wishlist" : "Removed from wishlist");
    } catch { toast.error("Failed"); }
  };

  const subtotal = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const cartCount = cart.reduce((s, c) => s + c.qty, 0);

  return (
    <Ctx.Provider value={{
      user, setUser, authLoading, login, register, logout, loadMe,
      cart, addToCart, updateQty, removeFromCart, clearCart, subtotal, cartCount,
      wishlistIds, toggleWishlist, settings
    }}>
      {children}
    </Ctx.Provider>
  );
}
